'use client';

import { type ReactNode } from 'react';
import { SmartsheetProjectTypes } from '~/server/enums';
import Button from '~/components/Button';
import Select from '~/components/Select';
import Spinner from '~/components/Spinner';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { ConnectIconTile } from './ConnectIconTile';

export type SmartsheetProjectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
};

const SOURCE_TYPES: Array<{
  value: SmartsheetProjectTypes;
  label: string;
  description: string;
}> = [
  {
    value: SmartsheetProjectTypes.SHEETS,
    label: 'Sheets',
    description: 'One sheet. Each row syncs as a task.',
  },
  {
    value: SmartsheetProjectTypes.REPORTS,
    label: 'Reports',
    description: 'A saved report. Rows can span multiple sheets.',
  },
];

export function SmartsheetLinkedStep({
  projectType,
  onProjectTypeChange,
  projects,
  selectedProjectId,
  onSelectedProjectChange,
  onContinue,
  onCancel,
  onDisconnect,
  isContinuing = false,
  isLoadingProjects = false,
  isDisconnecting = false,
  children,
}: {
  projectType: SmartsheetProjectTypes;
  onProjectTypeChange: (type: SmartsheetProjectTypes) => void;
  projects: SmartsheetProjectOption[];
  selectedProjectId: string;
  onSelectedProjectChange: (id: string) => void;
  onContinue: () => void;
  onCancel: () => void;
  onDisconnect?: () => void;
  isContinuing?: boolean;
  isLoadingProjects?: boolean;
  isDisconnecting?: boolean;
  children?: ReactNode;
}) {
  const isSheet = projectType === SmartsheetProjectTypes.SHEETS;
  const sourceNoun = isSheet ? 'sheet' : 'report';
  const busy = isContinuing || isDisconnecting;
  const canContinue = Boolean(selectedProjectId) && !busy && !isLoadingProjects;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center">
        <div className="mb-4">
          <ConnectIconTile src="/smartsheetLogo.png" alt="" size="lg" />
        </div>
        <h3 className="text-foreground text-lg font-semibold tracking-tight">
          Smartsheet Connected
        </h3>
        <p className="text-muted-foreground mt-1 max-w-sm text-sm leading-5">
          Choose how tasks are tracked, then pick a {sourceNoun} to link.
        </p>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-foreground text-[13px] font-medium tracking-tight">
          What type of Smartsheet tracks tasks?
        </legend>
        <RadioGroup
          value={projectType}
          onValueChange={(value) => onProjectTypeChange(value as SmartsheetProjectTypes)}
          className="gap-3"
        >
          {SOURCE_TYPES.map((type) => (
            <label key={type.value} className="flex cursor-pointer items-start gap-3">
              <RadioGroupItem value={type.value} className="mt-0.5 size-4" />
              <span className="min-w-0">
                <span className="text-foreground block text-[13px] font-medium tracking-tight">
                  {type.label}
                </span>
                <span className="text-muted-foreground mt-0.5 block text-xs leading-relaxed">
                  {type.description}
                </span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      {isLoadingProjects ? (
        <div className="flex justify-center py-2">
          <Spinner />
        </div>
      ) : (
        <Select
          label={`Select a ${isSheet ? 'Sheet' : 'Report'} to Link`}
          placeholder={`Available ${isSheet ? 'sheets' : 'reports'}`}
          options={projects}
          value={selectedProjectId}
          onChange={(e) => onSelectedProjectChange(e.target.value)}
        />
      )}

      {children}

      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          className="w-full"
          label="Continue"
          disabled={!canContinue}
          isLoading={isContinuing}
          onClick={onContinue}
        />
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          label="Cancel"
          disabled={busy}
          onClick={onCancel}
        />
        {onDisconnect ? (
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            label="Disconnect"
            disabled={busy}
            isLoading={isDisconnecting}
            onClick={onDisconnect}
          />
        ) : null}
      </div>
    </div>
  );
}
